// FIELD WORKER HOME — DESIGN PREVIEW C: "Sleek & Modern" (light, consumer-app polish)
// Self-contained mock. Not wired to real data.
import {
  MapPin, Navigation, Clock, ShieldAlert, ClipboardCheck, Trophy,
  FileSignature, ClipboardList, Palmtree, Home, Briefcase, Wrench,
  CircleUser, Bell, ChevronRight, CheckCircle2, Circle,
} from "lucide-react";

const fonts = { heading: { fontFamily: "'Outfit', sans-serif" }, body: { fontFamily: "'DM Sans', sans-serif" } };
const card = "bg-white rounded-3xl border border-slate-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.04)]";

export default function FieldPreview3() {
  return (
    <div className="min-h-screen bg-slate-200 py-6" data-testid="field-preview-3" style={fonts.body}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700&family=DM+Sans:wght@400;500;700&display=swap" rel="stylesheet" />
      <div className="max-w-[390px] mx-auto min-h-[840px] relative bg-slate-50 rounded-[2.5rem] overflow-hidden pb-28 shadow-xl">

        {/* Glass header */}
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-slate-100/60 px-5 pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Mon 28 July</div>
              <h1 style={fonts.heading} className="text-2xl font-semibold tracking-tight text-slate-900 mt-0.5">Mōrena, Ben</h1>
            </div>
            <div className="flex items-center gap-2.5">
              <button className="w-11 h-11 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center active:scale-95 transition-transform" data-testid="p3-bell">
                <Bell className="w-[18px] h-[18px] text-slate-600" />
              </button>
              <div className="w-11 h-11 rounded-full bg-[#FF5F15] text-white flex items-center justify-center font-semibold text-sm" style={fonts.heading}>BT</div>
            </div>
          </div>
          {/* Geofence pill */}
          <div className="mt-3 inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-full pl-3 pr-4 py-2" data-testid="p3-geofence">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold">On site — Kereru Rd</span>
            <span className="text-xs text-emerald-500 font-medium ml-1">Since 7:02am</span>
          </div>
        </div>

        <div className="px-5 pt-5 space-y-5">
          {/* Today's run sheet */}
          <div className={`${card} p-5`} data-testid="p3-runsheet">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Today's job</div>
                <div style={fonts.heading} className="text-lg font-semibold tracking-tight text-slate-900 mt-1">Stormwater upgrade — Stage 2</div>
                <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5"><MapPin className="w-3.5 h-3.5" /> 45 Kereru Rd, Papakura</div>
              </div>
              <button className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0 active:scale-95 transition-transform" data-testid="p3-directions">
                <Navigation className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4 space-y-1 divide-y divide-slate-50">
              {[["Excavate trench CH120–160", true], ["Lay 375mm RCRRJ pipe", true], ["Backfill & compact", false]].map(([t, done]) => (
                <div key={t} className="flex items-center gap-3 py-2.5">
                  {done
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                    : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                  <span className={`text-sm ${done ? "text-slate-400 line-through" : "text-slate-700 font-medium"}`}>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hours summary */}
          <div className={`${card} p-5`} data-testid="p3-hours">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">This week</div>
              <span className="text-xs font-medium text-slate-400">21 – 27 Jul</span>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div className="flex items-end gap-1.5">
                <span style={fonts.heading} className="text-5xl font-semibold tracking-tight text-slate-900 leading-none">32.5</span>
                <span className="text-sm text-slate-400 font-medium mb-1">hrs</span>
              </div>
              <div className="flex gap-4 text-right">
                <div>
                  <div style={fonts.heading} className="text-lg font-semibold text-emerald-600 leading-none">24.0</div>
                  <div className="text-[11px] text-slate-400 mt-1">Approved</div>
                </div>
                <div>
                  <div style={fonts.heading} className="text-lg font-semibold text-amber-500 leading-none">8.5</div>
                  <div className="text-[11px] text-slate-400 mt-1">Pending</div>
                </div>
              </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden flex">
              <div className="bg-emerald-500 rounded-l-full" style={{ width: "55%" }} />
              <div className="bg-amber-400" style={{ width: "19%" }} />
            </div>
            <button className="mt-4 w-full py-4 rounded-full bg-[#FF5F15] text-white font-semibold shadow-[0_8px_20px_rgba(255,95,21,0.25)] active:scale-95 transition-transform flex items-center justify-center gap-2" style={fonts.heading} data-testid="p3-log-hours">
              <Clock className="w-5 h-5" /> Log today's hours
            </button>
          </div>

          {/* Tomorrow */}
          <div className={`${card} p-4 flex items-center gap-4`} data-testid="p3-tomorrow">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex flex-col items-center justify-center shrink-0">
              <span className="text-[10px] font-semibold uppercase text-slate-400 leading-none">Tue</span>
              <span style={fonts.heading} className="text-xl font-semibold text-slate-900 leading-none mt-0.5">29</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Tomorrow</div>
              <div style={fonts.heading} className="font-semibold text-slate-900 truncate">Culvert install — SH22</div>
              <div className="text-xs text-slate-500 mt-0.5">06:30 start · Drury quarry side</div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </div>

          {/* Quick actions */}
          <div data-testid="p3-actions">
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400 mb-3 px-1">Quick actions</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                [ShieldAlert, "Hazard", "text-rose-500", "bg-rose-50"],
                [ClipboardCheck, "QA", "text-[#FF5F15]", "bg-orange-50"],
                [ClipboardList, "Forms", "text-sky-500", "bg-sky-50"],
                [FileSignature, "Variation", "text-violet-500", "bg-violet-50"],
                [Palmtree, "Leave", "text-emerald-500", "bg-emerald-50"],
                [Trophy, "Boards", "text-amber-500", "bg-amber-50"],
              ].map(([Icon, t, tc, bg]) => (
                <button key={t} className="bg-white rounded-2xl border border-slate-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.03)] py-4 flex flex-col items-center gap-2 active:scale-95 transition-transform">
                  <span className={`w-10 h-10 rounded-full ${bg} ${tc} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-semibold text-slate-700">{t}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Needs attention */}
          <div className={`${card} p-4 flex items-center gap-3`} data-testid="p3-attention">
            <span className="w-10 h-10 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5" />
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">2 timesheets pending</div>
              <div className="text-xs text-slate-500">Tap to review before Friday payroll</div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300" />
          </div>
        </div>

        {/* Glass bottom nav */}
        <div className="absolute bottom-0 inset-x-0 backdrop-blur-xl bg-white/85 border-t border-slate-100/60 grid grid-cols-6 pt-2.5 pb-5" data-testid="p3-bottomnav">
          {[[Home, "Home", true], [Briefcase, "Jobs"], [Clock, "Hours"], [ShieldAlert, "Safety"], [Wrench, "Toolbox"], [CircleUser, "Me"]].map(([Icon, l, active]) => (
            <div key={l} className="flex flex-col items-center gap-1">
              <Icon className={`w-[22px] h-[22px] ${active ? "text-[#FF5F15]" : "text-slate-400"}`} strokeWidth={active ? 2.4 : 2} />
              <span className={`text-[10px] font-semibold ${active ? "text-[#FF5F15]" : "text-slate-400"}`}>{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-center text-slate-500 text-xs font-mono mt-4 uppercase tracking-widest">Preview C — Sleek & Modern (light)</div>
    </div>
  );
}
